import { ConnectionPositionPair, Overlay, OverlayConfig, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';
import { Injector } from '@angular/core';
import { NvMultiPickerMenuContentComponent } from './nv-multi-picker-menu-content/nv-multi-picker-menu-content.component';
import { NvMultipickerMenuRef } from './nv-multi-picker-menu-content/nv-multipicker.ref';
import { IMultipickerMenuProps } from './nv-multi-picker.interface';

// based on
// https://stackblitz.com/edit/netanel-popover
export class NvMultipickerMenuService {
  private overlayRef: OverlayRef;
  public menuRef: NvMultipickerMenuRef;

  public get isOpen(): boolean {
    return this.menuRef && this.menuRef.isOpen();
  }

  constructor(private overlay: Overlay, private injector: Injector) {}

  public openMenu(originElem: HTMLElement, props: IMultipickerMenuProps): NvMultipickerMenuRef {
    if (!this.isOpen) {
      this.overlayRef = this.overlay.create(this.getOverlayConfig(originElem));
      this.menuRef = new NvMultipickerMenuRef(this.overlayRef, props); // create the reference object for the menu
      const menuPortal = new ComponentPortal(
        NvMultiPickerMenuContentComponent,
        null,
        this.createInjector(this.menuRef),
      ); // Create the portal and inject the reference object
      this.overlayRef.attach(menuPortal); // Attatch the portal to the overlay
    }
    return this.menuRef;
  }

  public closeMenu() {
    if (this.isOpen) {
      this.menuRef.closeMenu();
    }
  }

  public toggleMenu(originElem?: HTMLElement, props?: IMultipickerMenuProps) {
    if (this.overlayRef && this.overlayRef.hasAttached()) {
      this.closeMenu();
    } else {
      this.openMenu(originElem, props);
    }
    return this.menuRef.isOpen;
  }

  private createInjector(menuRef: NvMultipickerMenuRef) {
    const tokens = new WeakMap([[NvMultipickerMenuRef, menuRef]]);
    return new PortalInjector(this.injector, tokens);
  }

  private getOverlayConfig(origin: HTMLElement): OverlayConfig {
    return new OverlayConfig({
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      positionStrategy: this.getOverlayPosition(origin),
    });
  }

  private getOverlayPosition(origin: HTMLElement): PositionStrategy {
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(origin)
      .withPositions(this.getPositions())
      .withFlexibleDimensions(true)
      .withPush(false);
    return positionStrategy;
  }

  private getPositions(): ConnectionPositionPair[] {
    return [
      {
        offsetY: 8,
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        panelClass: 'nv-multi-picker-panel',
      },
      {
        offsetY: 8,
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
        panelClass: 'nv-multi-picker-panel',
      },
    ];
  }
}
